import React, { useState, useEffect, useCallback } from 'react';
import { Star, CheckCircle, XCircle, Clock, MessageSquare, User, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const ReviewManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected

  const fetchReviews = useCallback(async () => {
    try {
      const url = filter === 'all' 
        ? `${API}/api/admin/reviews`
        : `${API}/api/admin/reviews?status=${filter}`;
      
      const response = await axios.get(url);
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const updateReviewStatus = async (reviewId, status) => {
    try {
      await axios.put(`${API}/api/admin/reviews/${reviewId}/status`, { status });
      toast.success(`Review ${status} successfully`);
      fetchReviews();
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500"><CheckCircle size={12} className="mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500"><XCircle size={12} className="mr-1" /> Rejected</Badge>;
      default:
        return <Badge className="bg-amber-500"><Clock size={12} className="mr-1" /> Pending</Badge>;
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { value: 'all', label: 'All Reviews' },
          { value: 'pending', label: 'Pending' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' }
        ].map((tab) => (
          <Button
            key={tab.value}
            variant={filter === tab.value ? 'default' : 'outline'}
            onClick={() => setFilter(tab.value)}
            size="sm"
            className={filter === tab.value ? 'bg-teal-600' : ''}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gray-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{reviews.length}</p>
            <p className="text-sm text-gray-600">Total Reviews</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">
              {reviews.filter(r => r.status === 'pending').length}
            </p>
            <p className="text-sm text-gray-600">Pending</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">
              {reviews.filter(r => r.status === 'approved').length}
            </p>
            <p className="text-sm text-gray-600">Approved</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">
              {reviews.length > 0 
                ? (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)
                : '0'}
            </p>
            <p className="text-sm text-gray-600">Avg Rating</p>
          </CardContent>
        </Card>
      </div>

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No reviews found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <Card key={review.id} className="overflow-hidden">
              <CardContent className="p-0">
                <div className="flex flex-col md:flex-row">
                  {/* Review Content */}
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <User size={16} className="text-gray-400" />
                          <span className="font-semibold">{review.customer_name}</span>
                          {review.is_verified && (
                            <Badge variant="outline" className="text-xs">
                              <CheckCircle size={10} className="mr-1" /> Verified
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          {renderStars(review.rating)}
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {new Date(review.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      {getStatusBadge(review.status)}
                    </div>
                    
                    <p className="text-gray-700 mt-3 bg-gray-50 p-3 rounded-lg">
                      "{review.review_text}"
                    </p>
                    
                    <p className="text-xs text-gray-400 mt-2">
                      Service Date: {review.service_date}
                    </p>
                  </div>

                  {/* Actions */}
                  {review.status === 'pending' && (
                    <div className="bg-gray-50 p-4 flex flex-row md:flex-col gap-2 justify-center border-t md:border-t-0 md:border-l">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => updateReviewStatus(review.id, 'approved')}
                      >
                        <CheckCircle size={14} className="mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => updateReviewStatus(review.id, 'rejected')}
                      >
                        <XCircle size={14} className="mr-1" />
                        Reject
                      </Button>
                    </div>
                  )}

                  {review.status !== 'pending' && (
                    <div className="bg-gray-50 p-4 flex flex-row md:flex-col gap-2 justify-center border-t md:border-t-0 md:border-l">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateReviewStatus(review.id, 'pending')}
                      >
                        <Clock size={14} className="mr-1" />
                        Reset
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewManagement;
